import { Container, Text, onLinkClick } from '@cottonray/ui-web'
import { POLICIES, type PolicyDocument, type PolicySection } from './policies'

/**
 * 방침 문서 페이지에는 목록으로 나가는 링크도, 사이트 헤더·푸터도 두지 않는다 (docs/rules.md).
 * 이용자와 스토어 심사는 자기 서비스의 방침 주소(/privacy/<서비스>)로 들어오며,
 * 거기서 다른 서비스 목록이 노출될 통로를 만들지 않는다.
 *
 * 목록(/privacy)은 주소를 아는 운영자용이라 사이트 크롬을 그대로 쓴다.
 */

export function PolicyDoc({ policy }: { policy: PolicyDocument }) {
  return (
    <main className="doc">
      <Text variant="title">{policy.title}</Text>
      <Text variant="caption" tone="subtle" className="doc__meta">
        Effective {policy.effectiveDate} · Last revised {policy.lastRevisedDate}
      </Text>
      <Text tone="muted" className="doc__intro">
        {policy.intro}
      </Text>

      {policy.sections.map((section) => (
        <Section key={section.heading} section={section} />
      ))}
    </main>
  )
}

function Section({ section }: { section: PolicySection }) {
  return (
    <section>
      <Text variant="heading">{section.heading}</Text>
      {section.paragraphs?.map((paragraph) => <Text key={paragraph}>{paragraph}</Text>)}
      {section.bullets !== undefined && (
        <ul>
          {section.bullets.map((bullet) => (
            <li key={bullet}>
              <Text as="span">{bullet}</Text>
            </li>
          ))}
        </ul>
      )}
      {section.table !== undefined && (
        // 좁은 화면에서 표가 페이지를 밀지 않도록 표만 따로 가로 스크롤시킨다
        <div className="doc__table-scroll">
          <table className="doc__table">
            <thead>
              <tr>
                {section.table.headers.map((header) => (
                  <th key={header}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.table.rows.map((row) => (
                <tr key={row.join('|')}>
                  {row.map((cell, index) => (
                    <td key={index}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {section.note !== undefined && <Text tone="muted">{section.note}</Text>}
    </section>
  )
}

export function PrivacyIndex() {
  return (
    <section className="band band--tight">
      <Container>
        <div className="section__head">
          <Text variant="title">Privacy policies</Text>
          <Text tone="muted">
            Each service collects different data, so each keeps its own document. Select a service
            to open it.
          </Text>
        </div>
        <div className="doc-list">
          {POLICIES.map((policy) => (
            <a key={policy.slug} href={`/privacy/${policy.slug}`} onClick={onLinkClick}>
              <Text variant="heading">{policy.title}</Text>
              <Text variant="caption" tone="subtle">
                /privacy/{policy.slug} · Effective {policy.effectiveDate}
              </Text>
            </a>
          ))}
        </div>
      </Container>
    </section>
  )
}
